import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LogoWordmark } from "./Logo";
import { useLang } from "../context/LanguageContext";
import { Menu, X } from "lucide-react";
import { trackCtaClick } from "../lib/analytics";

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, end: true },
    { to: "/pendler", label: t.nav.pendler },
    { to: "/huseier", label: t.nav.huseier },
    { to: "/om-oss", label: t.nav.om },
    { to: "/kontakt", label: t.nav.kontakt },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-ink/85 backdrop-blur-xl border-b border-amber/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" className="amber-glow">
          <LogoWordmark />
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight transition-colors ${
                  isActive ? "text-amber" : "text-cream/80 hover:text-amber"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label="Toggle language"
            className="hidden sm:flex items-center gap-1.5 px-3 h-9 border border-amber/40 hover:border-amber text-cream hover:text-amber text-xs font-bold tracking-widest transition-colors"
          >
            <span className={lang === "no" ? "text-amber" : ""}>NO</span>
            <span className="text-cream/30">/</span>
            <span className={lang === "en" ? "text-amber" : ""}>EN</span>
          </button>

          <Link
            to="/pendler"
            data-testid="nav-cta-button"
            onClick={() => trackCtaClick("pendler", "nav")}
            className="hidden md:inline-flex items-center gap-1.5 h-10 px-5 bg-amber text-ink font-bold text-sm tracking-tight hover:bg-amber-hover transition-colors"
          >
            {t.nav.cta}
            <span className="text-base leading-none">→</span>
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-cream p-2"
            onClick={() => setOpen((p) => !p)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-ink/95 backdrop-blur-xl border-t border-amber/20" data-testid="mobile-menu">
          <nav className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`mobile-nav-link-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-xl font-display font-bold tracking-tight ${
                    isActive ? "text-amber" : "text-cream"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center justify-between pt-4 border-t border-amber/15">
              <button
                onClick={toggle}
                data-testid="mobile-lang-toggle"
                className="flex items-center gap-1.5 px-3 h-10 border border-amber/40 text-cream text-xs font-bold tracking-widest"
              >
                <span className={lang === "no" ? "text-amber" : ""}>NO</span>
                <span className="text-cream/30">/</span>
                <span className={lang === "en" ? "text-amber" : ""}>EN</span>
              </button>
              <Link
                to="/pendler"
                data-testid="mobile-cta-button"
                onClick={() => trackCtaClick("pendler", "mobile_nav")}
                className="inline-flex items-center gap-1.5 h-10 px-5 bg-amber text-ink font-bold text-sm"
              >
                {t.nav.cta} →
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
